
// Utilities for handling API keys - localStorage is still used here as specifically requested by user

/**
 * Get the appropriate API key based on provider type
 */
export function getAPIKey(providerType: 'openai' | 'perplexity', fallbackKey: string = ""): string {
  // First try to fetch from the standard token format
  const standardKey = localStorage.getItem(`${providerType}_token`);
  
  // If not found, try alternate formats that might be in use
  if (!standardKey) {
    // Check for legacy key format
    const legacyKey = localStorage.getItem(`${providerType}_api_key`);
    
    // Check for email provider token format (which may also store these keys)
    const emailProviderKey = localStorage.getItem(`email_provider_${providerType}`);
    
    console.log(`${providerType} key lookup: standardKey=${!!standardKey}, legacyKey=${!!legacyKey}, emailProviderKey=${!!emailProviderKey}, fallbackKey=${!!fallbackKey}`);
    
    // Return the first available key in order of preference
    return standardKey || legacyKey || emailProviderKey || fallbackKey;
  }
  
  return standardKey || fallbackKey;
}

/**
 * Save API key to localStorage in all known formats for compatibility
 */
export function saveAPIKey(providerType: 'openai' | 'perplexity', apiKey: string): void {
  // Store in all formats for maximum compatibility
  localStorage.setItem(`${providerType}_token`, apiKey);
  localStorage.setItem(`${providerType}_api_key`, apiKey);
  localStorage.setItem(`email_provider_${providerType}`, apiKey);
  console.log(`Saving ${providerType} API key to localStorage in all formats`);
}

/**
 * Clear API key from localStorage
 */
export function clearAPIKey(providerType: 'openai' | 'perplexity'): void {
  localStorage.removeItem(`${providerType}_token`);
  localStorage.removeItem(`${providerType}_api_key`); // Also remove legacy format
  localStorage.removeItem(`email_provider_${providerType}`); // Also remove email provider format
}

/**
 * Get all API keys from localStorage
 * This is useful for debugging and ensuring only API keys are stored
 */
export function getAllStoredApiKeys(): Record<string, string | null> {
  const result: Record<string, string | null> = {};
  
  // List of all possible API key storage keys
  const possibleKeys = [
    'openai_token',
    'openai_api_key',
    'perplexity_token',
    'perplexity_api_key',
    'email_provider_openai',
    'email_provider_perplexity',
    'openai_provider',
    'perplexity_provider'
  ];
  
  // Get values for all possible keys
  possibleKeys.forEach(key => {
    result[key] = localStorage.getItem(key);
  });
  
  // Log all keys for debugging
  console.log("Current stored API keys (presence only):", 
    Object.fromEntries(Object.entries(result).map(([k, v]) => [k, !!v])));
  
  return result;
}
